import React from "react";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice
} from "@watermarkinsights/ripple-react";
import "../../../common/Insights.scss";
import translate from "../../../../i18n/translate";
import { useIntl } from "react-intl";
import { CourseProgressData } from "./MissionAndOutcomesExportView";
import ApiError from "@/features/common/elements/ApiError";
import { OutcomesTerminology } from "@/contexts/AppContext";
import { useFormatMessage } from "@/features/common/helper";

export const popover = (amount: number, total: number | null) => {
  return `${amount}/${total}`;
};

interface CoursesChartProps {
  courseData: CourseProgressData;
  courseApiError: boolean;
  outcomesTerminology?: OutcomesTerminology | null;
}

export default function CoursesChart({
  courseData,
  courseApiError,
  outcomesTerminology
}: CoursesChartProps) {
  return (
    <div>
      <h3 className="title organization-progress">
        {translate("app.courseCard.courseProgress")}
      </h3>
      {!courseApiError ? (
        <>
          <p className="course totals">
            {translate("app.courseCard.courseTotal", {
              totalCourses: courseData.totalCourses
            })}
          </p>
          <div id="course-charts-html">
            <CourseProgressChart courseData={courseData} outcomesTerminology={outcomesTerminology} />
          </div>
        </>
      ) : (
        <ApiError containerClasses="api-error" />
      )}
    </div>
  );
}
interface CourseChartProps {
  courseData: CourseProgressData;
  outcomesTerminology?: OutcomesTerminology | null;
}
const CourseProgressChart = ({ courseData, outcomesTerminology }: CourseChartProps) => {
  const formatMessage = useFormatMessage();
  const created = useIntl().formatMessage({
    id: "app.drilldown.created"
  });

  const notCreated = useIntl().formatMessage({
    id: "app.drilldown.notCreated"
  });

  const mapped = useIntl().formatMessage({
    id: "app.drilldown.mapped"
  });

  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped"
  });

  const completedPopoverText = formatMessage(
    { id: "app.courseCard.coursesILOMapped" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );

  const notStartedPopoverText = formatMessage(
    { id: "app.courseCard.coursesILONotMapped" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const learningOutcomes = formatMessage(
    { id: "app.orgProgressCard.learningOutcomes" },
    { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
  );
  const iLOMapped = useIntl().formatMessage({
    id: "app.orgProgressCard.ILOMapped"
  });
  return (
    <WmProgressMonitor
      id="course-progress"
      print-mode={true}
      bar-labels-width="5rem"
    >
      <WmProgressIndicator label={learningOutcomes}>
        <WmProgressSlice
          legend={created}
          amount={courseData.courseLearningOutcomesComplete.toString()}
          popoverTitle={popover(
            courseData.courseLearningOutcomesComplete,
            courseData.totalCourses
          )}
          popoverText={completedPopoverText}
        />
        <WmProgressSlice
          legend={notCreated}
          amount={courseData.courseLearningOutcomesNotStarted.toString()}
          popoverTitle={popover(
            courseData.courseLearningOutcomesNotStarted,
            courseData.totalCourses
          )}
          popoverText={notStartedPopoverText}
        />
      </WmProgressIndicator>
      <WmProgressIndicator label={iLOMapped}>
        <WmProgressSlice
          legend={mapped}
          amount={courseData.courseIloComplete.toString()}
          popoverTitle={popover(
            courseData.courseIloComplete,
            courseData.totalCourses
          )}
          popoverText={completedPopoverText}
        />
        <WmProgressSlice
          legend={notMapped}
          amount={courseData.courseIloNotStarted.toString()}
          popoverTitle={popover(
            courseData.courseIloNotStarted,
            courseData.totalCourses
          )}
          popoverText={notStartedPopoverText}
        />
      </WmProgressIndicator>
    </WmProgressMonitor>
  );
};
